import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  IconButton,
  SelectChangeEvent
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { UserGroup, UserGroupFormData } from '../../types/userGroup';
import { userGroupsApi } from '../../services/userGroupService';

interface UserGroupFormProps {
  mode: 'create' | 'edit';
}

const UserGroupForm: React.FC<UserGroupFormProps> = ({ mode }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // حالة النموذج
  const [formData, setFormData] = useState<UserGroupFormData>({
    name: '',
    description: '',
    status: 'active'
  });
  
  // حالات أخرى
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // تحميل بيانات المجموعة عند التعديل
  useEffect(() => {
    const loadUserGroup = async () => {
      if (mode === 'edit' && id) {
        try {
          setLoading(true);
          const userGroup = await userGroupsApi.getUserGroup(id);
          setFormData({
            name: userGroup.name,
            description: userGroup.description || '',
            status: userGroup.status
          });
        } catch (err) {
          console.error('Error loading user group:', err);
          setError('حدث خطأ أثناء تحميل بيانات المجموعة');
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadUserGroup();
  }, [id, mode]);
  
  // معالجة تغيير حقول النص
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // معالجة تغيير حقول الاختيار
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };
  
  // التحقق من البيانات
  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('اسم المجموعة مطلوب');
      return false;
    }
    
    return true;
  };
  
  // حفظ البيانات
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من البيانات
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      if (mode === 'create') {
        // إنشاء مجموعة جديدة
        await userGroupsApi.createUserGroup(formData);
        setSuccess('تم إنشاء المجموعة بنجاح');
        // إعادة تعيين النموذج
        setFormData({
          name: '',
          description: '',
          status: 'active'
        });
        // العودة للقائمة بعد وقت قصير
        setTimeout(() => {
          navigate('/admin/user-groups');
        }, 1500);
      } else if (id) {
        // تحديث المجموعة الحالية
        await userGroupsApi.updateUserGroup(id, formData);
        setSuccess('تم تحديث المجموعة بنجاح');
      }
    } catch (err: any) {
      console.error('Error saving user group:', err);
      setError(err.response?.data?.error || 'حدث خطأ أثناء حفظ البيانات');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          borderRadius: 2,
          boxShadow: theme.shadows[2],
          mb: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* شريط زخرفي علوي */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 8,
            bgcolor: 'primary.main',
          }}
        />
        
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, mt: 1 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon fontSize="large" color="primary" />
            {mode === 'create' ? 'إنشاء مجموعة مستخدمين جديدة' : 'تعديل مجموعة المستخدمين'}
          </Typography>
          
          <IconButton 
            color="primary" 
            onClick={() => navigate('/admin/user-groups')}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
        
        {/* رسائل النجاح والخطأ */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {/* تحميل البيانات */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              جاري تحميل البيانات...
            </Typography>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* اسم المجموعة */}
              <Grid item xs={12} md={6}>
                <TextField
                  name="name"
                  label="اسم المجموعة"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  inputProps={{ maxLength: 100 }}
                  InputProps={{ sx: { borderRadius: 2 } }}
                  disabled={saving}
                />
              </Grid>
              
              {/* حالة المجموعة */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="status-label">الحالة</InputLabel>
                  <Select
                    labelId="status-label"
                    name="status"
                    value={formData.status}
                    onChange={handleSelectChange}
                    label="الحالة"
                    disabled={saving}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="active">نشط</MenuItem>
                    <MenuItem value="inactive">غير نشط</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* وصف المجموعة */}
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="وصف المجموعة"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  InputProps={{ sx: { borderRadius: 2 } }}
                  helperText="وصف اختياري لتوضيح الغرض من هذه المجموعة"
                  disabled={saving}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 4 }} />
            
            {/* أزرار الإجراءات */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.2 }}
              >
                {saving ? 'جاري الحفظ...' : 'حفظ البيانات'}
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/admin/user-groups')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.2 }}
              >
                إلغاء
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default UserGroupForm;